/**
 * racer.ts — wires the pseudo-3D racing engine to the racer page DOM.
 *
 * Inputs: keyboard (A/D/←/→ + Space for nitro), touch (tap zones), and
 * device tilt (iOS requires a permission grant — surface a "Tap to Start"
 * gesture that requests it). Ends with a fade-to-black "Then & Now"
 * end-card that auto-plays a placeholder PS5 video.
 */
import { createEngine, preloadSprites, ROUND_MS_PUBLIC, type StageTheme } from "./racer-engine";
import { requestTiltPermission, startTilt, stopTilt } from "./tilt";
import { createEngineAudio } from "./audio";
import { track } from "./analytics";

(function () {
  "use strict";

  const canvas = document.getElementById("game-canvas") as HTMLCanvasElement | null;
  if (!canvas) return;

  const scoreEl = document.getElementById("hud-score") as HTMLElement;
  const speedEl = document.getElementById("hud-speed") as HTMLElement;
  const timeEl  = document.getElementById("hud-time") as HTMLElement;
  const missEl  = document.getElementById("hud-miss") as HTMLElement;
  // Stage block was dropped from the racer HUD — keep the lookup defensive
   // so the engine's stage callback still no-ops cleanly when null.
  const stageEl = document.getElementById("hud-stage") as HTMLElement | null;
  const stageFlash = document.getElementById("stage-flash") as HTMLElement;
  const endcard = document.getElementById("endcard") as HTMLElement;
  const endcardStatus = document.getElementById("endcard-status") as HTMLElement;
  const endcardScore = document.getElementById("endcard-score") as HTMLElement;
  const endcardVideo = document.getElementById("endcard-video") as HTMLVideoElement;
  const startOverlay = document.getElementById("start-overlay") as HTMLElement;
  const startBtn = document.getElementById("start-btn") as HTMLButtonElement;
  const nitroBtn = document.getElementById("nitro-btn") as HTMLButtonElement;
  const muteBtn  = document.getElementById("mute-btn")  as HTMLButtonElement | null;
  const muteIconOn  = document.getElementById("mute-icon-on")  as HTMLElement | null;
  const muteIconOff = document.getElementById("mute-icon-off") as HTMLElement | null;
  let muted = true;
  muteBtn?.addEventListener("click", () => {
    muted = !muted;
    audio.mute(muted);
    if (muteIconOn)  muteIconOn.style.display  = muted ? "none" : "";
    if (muteIconOff) muteIconOff.style.display = muted ? "" : "none";
    muteBtn.setAttribute("aria-label", muted ? "Unmute" : "Mute");
    track("mute_toggle", { game: "racer", muted });
  });

  const keys = { left: false, right: false, nitro: false };
  // Continuous finger-slide steering: when the user is touching the screen,
  // touchTilt holds a value in [-1, 1] derived from finger X relative to
  // viewport center. null means no finger is down — fall back to keyboard.
  let touchTilt: number | null = null;
  let touchNitro = false;

  // Web Audio engine drone — lazy AudioContext, started on first user tap.
  const audio = createEngineAudio();
  (window as unknown as { muteRacer: (on: boolean) => void }).muteRacer = (on) => audio.mute(on);

  const engine = createEngine(canvas, {
    onScore: (s) => { scoreEl.textContent = s.toLocaleString(); },
    onSpeed: (s) => { speedEl.textContent = String(s); audio.setSpeed(s); },
    onCheckpoint: () => { /* sfx hook */ },
    onMiss: (m) => {
      missEl.textContent = `${m}/3`;
      missEl.parentElement?.classList.add("danger");
      setTimeout(() => missEl.parentElement?.classList.remove("danger"), 250);
    },
    onStage: (n, theme: StageTheme) => {
      if (n === 0) return;
      stageFlash.textContent = `STAGE ${n + 1} — ${theme.name}`;
      stageFlash.classList.add("show");
      stageFlash.style.color = theme.horizon;
      setTimeout(() => stageFlash.classList.remove("show"), 1500);
    },
    onComplete: (s) => endGame(s, "COURSE CLEAR"),
    onGameOver: (s) => endGame(s, "GAME OVER"),
  });
  // Expose for QA / debugging in the browser console.
  (window as unknown as { racerEngine: unknown }).racerEngine = engine;

  let gameStartedAt = 0;
  function endGame(finalScore: number, label: string) {
    stopTilt();
    audio.stop();
    endcardStatus.textContent = label;
    endcardScore.textContent = finalScore.toLocaleString();
    endcard.classList.add("show");
    track("game_end", {
      game: "racer",
      score: finalScore,
      outcome: label === "COURSE CLEAR" ? "complete" : "fail",
      durationMs: gameStartedAt ? Math.round(performance.now() - gameStartedAt) : null,
    });
    // Try autoplay — muted + playsinline allows it on iOS / Android.
    endcardVideo.play().catch(() => { /* user can tap to play */ });
  }

  function tickHud() {
    const t = engine.getTimeLeft();
    timeEl.textContent = (t / 1000).toFixed(1) + "s";
    if (stageEl) stageEl.textContent = String(engine.getStage() + 1);
    if (t > 0) requestAnimationFrame(tickHud);
  }

  // Unified 30Hz control loop — merges keyboard + continuous touch + tilt.
  // Priority: keyboard (when an arrow/A/D is held) > finger slide > device tilt.
  // Keys always win when pressed — on desktop the tilt API resolves "granted"
  // even though no real sensor is attached, so we can't gate keyboard on tilt.
  // Track whether the device-orientation handler has actually fired recently.
  // requestTiltPermission() resolves "granted" on desktops where no real
  // sensor exists, so we can't trust `tiltActive` alone — we need to see
  // an event before yielding control to tilt.
  let lastTiltEventAt = 0;
  const TILT_FEED_TIMEOUT_MS = 400;
  function controlLoop() {
    const keyTilt = (keys.right ? 1 : 0) - (keys.left ? 1 : 0);
    const tiltFresh = (performance.now() - lastTiltEventAt) < TILT_FEED_TIMEOUT_MS;
    if (keyTilt !== 0) {
      engine.setTilt(keyTilt);
    } else if (touchTilt !== null) {
      engine.setTilt(touchTilt);
    } else if (!tiltFresh) {
      // No keys, no finger, and no live tilt event — recenter the car so
      // the sprite snaps back to the straight frame.
      engine.setTilt(0);
    }
    // If a real tilt event fired recently, leave it alone — the
    // deviceorientation handler is feeding setTilt directly.
    const nitroOn = keys.nitro || touchNitro;
    engine.setNitro(nitroOn);
    audio.setNitro(nitroOn);
  }
  let tiltActive = false;

  // --- input bindings ------------------------------------------------
  window.addEventListener("keydown", (e) => {
    if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") { keys.left = true; e.preventDefault(); }
    if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") { keys.right = true; e.preventDefault(); }
    if (e.key === " " || e.key === "Shift" || e.key === "w" || e.key === "W") { keys.nitro = true; e.preventDefault(); }
  });
  window.addEventListener("keyup", (e) => {
    if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") keys.left = false;
    if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") keys.right = false;
    if (e.key === " " || e.key === "Shift" || e.key === "w" || e.key === "W") keys.nitro = false;
    // On release, snap the car straight immediately so the sprite returns
    // to the center frame without waiting for the next control-loop tick.
    if (!keys.left && !keys.right && touchTilt === null) engine.setTilt(0);
  });

  // Continuous touch-slide steering — finger X across the overlay maps to
  // a normalized tilt value. Touch anywhere on the steer area, drag side to
  // side. Works alongside the device-tilt API and keyboard keys.
  function bindTouchSteer() {
    const zone = document.getElementById("touch-steer");
    if (!zone) return;
    const computeTilt = (clientX: number) => {
      const rect = canvas.getBoundingClientRect();
      // Map across ~70% of viewport — full-lock at 35% from center either way
      const half = rect.width * 0.5;
      const dead = rect.width * 0.02;            // small dead zone at center
      const offset = clientX - (rect.left + half);
      const dir = Math.sign(offset);
      const mag = Math.max(0, Math.abs(offset) - dead) / (half * 0.7);
      return Math.max(-1, Math.min(1, dir * mag));
    };
    const onDown = (e: PointerEvent) => {
      zone.setPointerCapture(e.pointerId);
      touchTilt = computeTilt(e.clientX);
      e.preventDefault();
    };
    const onMove = (e: PointerEvent) => {
      if (touchTilt === null) return;             // only track while pressed
      touchTilt = computeTilt(e.clientX);
      e.preventDefault();
    };
    const onUp = (e: PointerEvent) => {
      touchTilt = null;
      // Explicitly recenter — on desktop / browsers with no real tilt
      // sensor, `tiltActive` is true (permission "granted" by default)
      // and controlLoop won't recenter for us. On a real mobile device
      // the deviceorientation handler will overwrite this on its next
      // tick, so this is a no-op there.
      engine.setTilt(0);
      try { zone.releasePointerCapture(e.pointerId); } catch { /* may already be released */ }
    };
    zone.addEventListener("pointerdown", onDown);
    zone.addEventListener("pointermove", onMove);
    zone.addEventListener("pointerup", onUp);
    zone.addEventListener("pointercancel", onUp);
    zone.addEventListener("pointerleave", onUp);
  }
  bindTouchSteer();

  nitroBtn?.addEventListener("pointerdown", () => (touchNitro = true));
  nitroBtn?.addEventListener("pointerup",   () => (touchNitro = false));
  nitroBtn?.addEventListener("pointerleave",() => (touchNitro = false));

  // Kick off sprite preload immediately — by the time the user taps Start
  // most assets should be ready. start() works either way (procedural fallback).
  // Catch any rejection so an asset failure can't strand the start button.
  const spritesReady = preloadSprites().catch((e) => { console.warn("preloadSprites rejected", e); });

  // --- start gesture (required for iOS tilt permission) -------------
  startBtn?.addEventListener("click", async () => {
    const orig = startBtn.textContent;
    startBtn.textContent = "LOADING…";
    startBtn.disabled = true;
    try {
      const perm = await requestTiltPermission().catch(() => "denied" as const);
      track("tilt_permission", { state: perm });
      // Race preload against a 4s watchdog so an asset hang can never
      // permanently freeze the Start button on Safari.
      await Promise.race([
        spritesReady,
        new Promise<void>((resolve) => setTimeout(resolve, 4000)),
      ]);
      if (perm === "granted") {
        tiltActive = true;
        startTilt((v) => { lastTiltEventAt = performance.now(); engine.setTilt(v); });
      }
      startOverlay.style.display = "none";
      engine.start();
      gameStartedAt = performance.now();
      track("game_start", { game: "racer" });
      audio.start();   // begins the engine drone — needs the user gesture
      audio.mute(muted); // honor the default-muted state so the drone starts silent
      requestAnimationFrame(tickHud);
      setInterval(controlLoop, 33);
    } catch (e) {
      console.warn("start failed", e);
    } finally {
      startBtn.textContent = orig;
      startBtn.disabled = false;
    }
  });

  // Endcard CTAs — track each click so the campaign report can break
  // down the post-game funnel (Shop / Replay / cross-promo). The Shop
  // and Runner pills are <a> tags; let the browser handle navigation
  // after the track() call fires.
  document.getElementById("ec-again")?.addEventListener("click", () => {
    track("endcard_cta", { game: "racer", cta: "replay" });
    location.reload();
  });
  document
    .querySelector<HTMLAnchorElement>(".endcard .pill--shop")
    ?.addEventListener("click", (e) => {
      const a = e.currentTarget as HTMLAnchorElement;
      track("endcard_cta", { game: "racer", cta: "shop", href: a.href });
    });
  document
    .querySelector<HTMLAnchorElement>(".endcard .pill--runner")
    ?.addEventListener("click", () => {
      track("endcard_cta", { game: "racer", cta: "runner" });
    });

  window.addEventListener("resize", () => engine.resize());
  window.addEventListener("orientationchange", () => setTimeout(() => engine.resize(), 200));

  // Expose round length for any UI element that wants it.
  (window as unknown as Record<string, unknown>).ROUND_MS = ROUND_MS_PUBLIC;
})();
